"use client";

import { FadeIn } from "@/components/ui/motion";
import { AnimatePresence, motion } from "framer-motion";
import { BrainCircuit, Building2, CheckCircle2, Rocket, Zap } from "lucide-react";
import { useEffect, useState } from "react";

const missions = [
    {
        id: "mvp",
        title: "Startup / MVP",
        icon: Rocket,
        color: "text-purple-600",
        description: "Go from napkin sketch to market-ready product in record time.",
        timeline: "4-8 Weeks",
        focus: ["Speed to Market", "Core Features", "User Feedback"],
        stack: ["Next.js", "Supabase", "Firebase", "TailwindCSS"],
        outcome: "A scalable foundation ready for your first 10,000 users.",
    },
    {
        id: "enterprise",
        title: "Scale / Enterprise",
        icon: Building2,
        color: "text-blue-600",
        description: "Robust, high-availability systems for complex business needs.",
        timeline: "3-6+ Months",
        focus: ["Security & Audits", "Microservices", "Compliance"],
        stack: ["Go (Golang)", "Node.js", "Kubernetes", "AWS / GCP"],
        outcome: "Enterprise-grade infrastructure with 99.9% uptime SLA.",
    },
    {
        id: "ai",
        title: "AI Transformation",
        icon: BrainCircuit,
        color: "text-fuchsia-600",
        description: "Inject intelligence into your workflows with custom AI solutions.",
        timeline: "Custom Timeline",
        focus: ["Process Automation", "Fine-tuning & RAG", "Data Privacy"],
        stack: ["Python", "OpenAI API", "Claude", "Vector DBs"],
        outcome: "Automated workflows and 10x operational efficiency.",
    },
];

export default function MissionControl() {
    const [activeMission, setActiveMission] = useState(missions[0]);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    // Auto-cycle through missions if the user hasn't clicked manually
    useEffect(() => {
        if (!isAutoPlaying) return;

        const cycle = () => {
            setActiveMission((current) => {
                const currentIndex = missions.findIndex((m) => m.id === current.id);
                const nextIndex = (currentIndex + 1) % missions.length;
                return missions[nextIndex];
            });
        };

        const timer = setInterval(cycle, 5000);
        return () => clearInterval(timer);
    }, [isAutoPlaying]);

    const handleTabClick = (mission: typeof missions[0]) => {
        setIsAutoPlaying(false); // Pause auto-rotation when user takes control
        setActiveMission(mission);
    };

    return (
        <section className="py-20 relative z-10 bg-transparent">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <FadeIn>
                    <div className="mb-12">
                        <h2 className="text-3xl font-black mb-4 flex items-center gap-2.5 tracking-tight">
                            <Zap className="h-6 w-6 text-yellow-500 fill-yellow-500" />
                            Mission Control
                        </h2>
                        <p className="text-gray-500 max-w-2xl text-lg font-medium leading-relaxed">
                            Select your operational stage to view our blueprint, execution timeline, and tech stack alignment.
                        </p>
                    </div>
                </FadeIn>

                {/* Horizontal Segmented Control Selector */}
                <FadeIn>
                    <div className="flex bg-gray-100/70 backdrop-blur-md p-1.5 rounded-2xl border border-zinc-200/50 w-fit mb-8 select-none font-sans">
                        {missions.map((mission) => {
                            const isActive = activeMission.id === mission.id;
                            return (
                                <button
                                    key={mission.id}
                                    onClick={() => handleTabClick(mission)}
                                    className={`relative px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center gap-2 cursor-pointer z-10 overflow-hidden
                                        ${isActive ? "text-white" : "text-gray-500 hover:text-black"}`}
                                >
                                    <mission.icon className="h-4 w-4 shrink-0" />
                                    <span>{mission.title}</span>
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeMissionTab"
                                            className="absolute inset-0 bg-black rounded-xl z-[-1] shadow-md"
                                            transition={{ type: "spring", stiffness: 350, damping: 28 }}
                                        />
                                    )}
                                    {isActive && isAutoPlaying && (
                                        <motion.div
                                            initial={{ width: "0%" }}
                                            animate={{ width: "100%" }}
                                            transition={{ duration: 5, ease: "linear" }}
                                            key={mission.id}
                                            className="absolute bottom-0 left-0 h-[2px] bg-purple-500 z-20"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </FadeIn>

                {/* Details Display Panel */}
                <div className="w-full">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeMission.id}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="bg-white/40 border border-zinc-200/80 backdrop-blur-lg rounded-[32px] p-8 md:p-10 shadow-lg relative overflow-hidden"
                        >
                            {/* Subtle Background Watermark Icon */}
                            <div className="absolute top-0 right-0 p-8 opacity-[0.02] pointer-events-none">
                                <activeMission.icon className="w-72 h-72 text-black" />
                            </div>

                            <div className="relative z-10 flex flex-col justify-between h-full">
                                {/* Header badge */}
                                <div className="mb-6 flex items-center">
                                    <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-gray-100/80 border border-zinc-200/50 text-gray-600">
                                        Est. Timeline: {activeMission.timeline}
                                    </span>
                                </div>

                                {/* Headline */}
                                <h3 className="text-3xl md:text-4xl font-light tracking-tight text-gray-900 mb-8 max-w-3xl leading-snug">
                                    {activeMission.description}
                                </h3>

                                {/* Grid Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Core Focus Checklist */}
                                    <div className="bg-white/40 border border-zinc-200/50 p-6 rounded-2xl shadow-sm">
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-4">Core Focus</h4>
                                        <ul className="space-y-3.5">
                                            {activeMission.focus.map((item, i) => (
                                                <li key={i} className="flex items-center gap-3 text-gray-700 font-semibold text-sm">
                                                    <CheckCircle2 className={`h-4.5 w-4.5 ${activeMission.color} shrink-0`} />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Tech Arsenal */}
                                    <div className="bg-white/40 border border-zinc-200/50 p-6 rounded-2xl shadow-sm">
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-4">Tech Arsenal</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {activeMission.stack.map((item, i) => (
                                                <span key={i} className="px-3 py-1.5 bg-white border border-gray-200/80 rounded-xl text-xs font-bold text-gray-700 shadow-sm">
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Outcome footer */}
                                <div className="mt-8 pt-8 border-t border-zinc-200/50">
                                    <h4 className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Expected Outcome</h4>
                                    <p className="text-lg font-medium text-gray-800 italic leading-relaxed">
                                        &ldquo;{activeMission.outcome}&rdquo;
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
